
import React, { useState, useEffect } from 'react';
import { getStudioApiKey } from '../utils/audio';

interface KeyManagementProps {
  ui: any;
}

const KeyManagement: React.FC<KeyManagementProps> = ({ ui }) => {
  const t = ui.keys;
  const [activeKey, setActiveKey] = useState('');
  const [newKey, setNewKey] = useState('');
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    setActiveKey(getStudioApiKey());
  }, []);

  const handleSave = () => {
    if (!newKey.trim()) return;
    const keys = { gemini: newKey.trim() };
    localStorage.setItem('groovefx_keys', JSON.stringify(keys));
    setActiveKey(newKey.trim());
    setIsSaved(true);
    setNewKey('');
    setTimeout(() => setIsSaved(false), 3000);
  };

  const handleClear = () => {
    localStorage.removeItem('groovefx_keys');
    setActiveKey('');
    setNewKey('');
  };

  const hasKey = !!activeKey;

  return (
    <div className="h-full flex flex-col space-y-12 max-w-7xl mx-auto animate-in fade-in slide-in-from-bottom-6 duration-700 pb-20 text-left">
      <div className="text-center space-y-6">
        <h2 className="text-5xl md:text-7xl font-black text-white tracking-tighter leading-tight uppercase">{t.title}</h2>
        <p className="text-xl text-gray-400 font-light max-w-3xl mx-auto leading-relaxed">{t.subtitle}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="p-10 rounded-[3rem] bg-black/40 border border-white/10 space-y-8 flex flex-col justify-between shadow-3xl backdrop-blur-2xl">
           <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold text-white uppercase tracking-widest">{t.label}</h3>
                <i className="fa-brands fa-google text-groove-accent text-3xl"></i>
              </div>
              <p className="text-xs text-gray-400 font-light leading-relaxed">{t.disclaimer}</p>
              
              <div className="space-y-4 pt-4">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Inject Gemini API Key</label>
                <div className="flex gap-4">
                  <input 
                    type="password" 
                    value={newKey} 
                    onChange={(e) => setNewKey(e.target.value)} 
                    placeholder="Enter your API Key..." 
                    className="flex-1 bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white outline-none focus:border-groove-accent transition-all text-sm font-mono" 
                  />
                  <button 
                    onClick={handleSave}
                    className={`px-8 py-4 rounded-2xl font-black uppercase tracking-widest text-[10px] transition-all ${isSaved ? 'bg-green-500 text-white' : 'bg-white text-black hover:bg-groove-pink hover:text-white'}`}
                  >
                    {isSaved ? 'Synced' : 'Sync'}
                  </button>
                </div>
              </div>
           </div>
           
           <div className="flex flex-col gap-4 border-t border-white/5 pt-8">
              <div className={`px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] border flex items-center justify-between ${hasKey ? 'bg-green-500/10 text-green-500 border-green-500/30' : 'bg-red-500/10 text-red-500 border-red-500/30'}`}>
                <span>{hasKey ? t.connected : t.missing}</span>
                <div className="flex items-center gap-4">
                  {hasKey && (
                    <button onClick={handleClear} className="text-slate-500 hover:text-red-500 transition-colors">
                      <i className="fa-solid fa-trash-can"></i>
                    </button>
                  )}
                  <i className={`fa-solid ${hasKey ? 'fa-link' : 'fa-link-slash'}`}></i>
                </div>
              </div>
              <p className="text-[9px] text-slate-500 uppercase tracking-widest">Protocol Handshake: {hasKey ? 'Authorized' : 'Pending'}</p>
           </div>
        </div>
        
        <div className="p-10 rounded-[3rem] bg-white/[0.02] border border-white/5 space-y-6 flex flex-col justify-center text-center opacity-40 grayscale">
            <h3 className="text-sm font-black text-slate-500 uppercase tracking-widest">Anthropic / OpenAI Vault</h3>
            <p className="text-[10px] text-slate-600 uppercase tracking-widest">Pipeline development in progress</p>
        </div>
      </div>
      
      <div className="bg-white/5 p-12 rounded-[4rem] border border-white/10 text-center space-y-8 max-w-3xl mx-auto">
        <h4 className="text-xl font-black text-white uppercase tracking-widest">Where to find your key?</h4>
        <p className="text-sm text-slate-400 leading-relaxed font-light">You can generate a free or paid API key at the <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-groove-accent hover:underline">Google AI Studio</a>. Paste the key above to enable high-fidelity multimodal interactions.</p>
      </div>
    </div>
  );
};

export default KeyManagement;
